var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import createClient from "openapi-fetch";
export class DirectoryClient {
    constructor(getToken, baseUrl) {
        this.client = createClient({ baseUrl });
        this.token = getToken;
    }
    buildAuthHeader() {
        return __awaiter(this, void 0, void 0, function* () {
            return { Authorization: `Bearer ${yield this.token()}` };
        });
    }
    getUser(tenant, directory_uuid) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data, // only present if 2XX response
            error, // only present if 4XX or 5XX response
             } = yield this.client.GET("/svc/directory/{tenant}/user/{directory_uuid}", {
                params: {
                    path: {
                        tenant,
                        directory_uuid,
                    },
                    query: {},
                },
                headers: yield this.buildAuthHeader(),
            });
            return { data, error };
        });
    }
    getUsersForTenant(tenant, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data, // only present if 2XX response
            error, // only present if 4XX or 5XX response
             } = yield this.client.GET("/svc/directory/{tenant}/users", {
                params: {
                    path: {
                        tenant,
                    },
                    query: {
                        email: options === null || options === void 0 ? void 0 : options.email,
                        givenName: options === null || options === void 0 ? void 0 : options.givenName,
                        familyName: options === null || options === void 0 ? void 0 : options.familyName,
                        role: options === null || options === void 0 ? void 0 : options.role,
                        includeArchived: options === null || options === void 0 ? void 0 : options.includeArchived,
                        attributes: options === null || options === void 0 ? void 0 : options.attributes,
                    },
                },
                headers: yield this.buildAuthHeader(),
            });
            return { data, error };
        });
    }
    createOrUpdateUser(user) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data, // only present if 2XX response
            error, // only present if 4XX or 5XX response
             } = yield this.client.PUT("/svc/directory/user", {
                body: user,
                headers: yield this.buildAuthHeader(),
            });
            return { data, error };
        });
    }
    // Useful when changing roles or adding a user to a new tenant.
    // Performs less authorization checks than createOrUpdateUser.
    modifyUserMemberships(directory_uuid, tenants) {
        return __awaiter(this, void 0, void 0, function* () {
            const body = {
                directory_uuid,
                tenants,
            };
            const { data, // only present if 2XX response
            error, // only present if 4XX or 5XX response
             } = yield this.client.PUT("/svc/directory/user", {
                body,
                headers: yield this.buildAuthHeader(),
            });
            return { data, error };
        });
    }
    // returns the profile of the user who is currently authenticated
    whoAmI() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.client.GET("/svc/directory/me", {
                headers: yield this.buildAuthHeader(),
            });
        });
    }
    // returns the hubspot ID token for the user who is currently authenticated
    hubspotId() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.client.GET("/svc/directory/hubspot", {
                headers: yield this.buildAuthHeader(),
            });
        });
    }
    // updates the profile of the user who is currently authenticated
    // Note that the body object will patch the existing profile, so only send the fields to update.
    updateSelf(body) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data, // only present if 2XX response
            error, // only present if 4XX or 5XX response
             } = yield this.client.PATCH("/svc/directory/me", {
                body,
                headers: yield this.buildAuthHeader(),
            });
            return { data, error };
        });
    }
    /**
     * Uploads a CSV file containing user data for a tenant.
     *
     * This method sends a `multipart/form-data` request to the server with the CSV file using the
     * `svc/directory/tenants/{tenant}/users/upload-csv` endpoint.
     *
     * @param tenant - The unique identifier for the tenant.
     * @param file - The CSV file to be uploaded. Must be a valid CSV file.
     * @returns A promise resolving to the CSV upload response.
     */
    uploadCsvToDirectory(tenant, file) {
        return __awaiter(this, void 0, void 0, function* () {
            const formData = new FormData();
            formData.append("file", file);
            const { data, error } = yield this.client.POST("/svc/directory/tenants/{tenant}/users/upload-csv", {
                params: {
                    path: { tenant },
                },
                headers: yield this.buildAuthHeader(),
                body: formData,
            });
            return { data, error };
        });
    }
    /**
     * Archives multiple users in bulk for a tenant.
     *
     * @param tenant - The unique identifier for the tenant.
     * @param directoryUuids - Array of user directory UUIDs to archive
     * @returns A promise resolving to the archive response with succeeded/failed results
     */
    archiveUsers(tenant, directoryUuids) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data, // only present if 2XX response
            error, // only present if 4XX or 5XX response
             } = yield this.client.POST("/svc/directory/user/archive", {
                body: {
                    tenant,
                    directory_uuids: directoryUuids,
                },
                headers: yield this.buildAuthHeader(),
            });
            return { data, error };
        });
    }
    /**
     * Unarchives multiple users in bulk for a tenant.
     *
     * @param tenant - The unique identifier for the tenant.
     * @param directoryUuids - Array of user directory UUIDs to unarchive
     * @returns A promise resolving to the archive response with succeeded/failed results
     */
    unarchiveUsers(tenant, directoryUuids) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data, // only present if 2XX response
            error, // only present if 4XX or 5XX response
             } = yield this.client.POST("/svc/directory/user/unarchive", {
                body: {
                    tenant,
                    directory_uuids: directoryUuids,
                },
                headers: yield this.buildAuthHeader(),
            });
            return { data, error };
        });
    }
    // Deletes a tenant by name. Can only be done by SJ Admin.
    deleteTenant(tenant) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data, // only present if 2XX response
            error, // only present if 4XX or 5XX response
             } = yield this.client.DELETE("/svc/directory/tenant/{tenant}", {
                params: {
                    path: {
                        tenant,
                    },
                },
                headers: yield this.buildAuthHeader(),
            });
            return { data, error };
        });
    }
}
//# sourceMappingURL=directory.js.map